import { useTranslation } from 'react-i18next'
import { DIMENSION_CONFIG, getGrade } from './shared/scoreUtils'
import type { AuditReport } from '@/lib/api/amazon-listing'

interface ScoreDashboardProps {
  scores: AuditReport['scores']
}

export function ScoreDashboard({ scores }: ScoreDashboardProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.scores.${key}`)
  const overallGrade = getGrade(scores.overall)

  const dimensions = Object.entries(DIMENSION_CONFIG)
    .filter(([key]) => key in scores.dimensions)
    .map(([key]) => ({
      key,
      score: scores.dimensions[key] ?? 0,
      grade: getGrade(scores.dimensions[key] ?? 0),
    }))

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      {/* Overall score hero */}
      <div className="flex items-center gap-4 mb-5">
        <div className={`w-20 h-20 rounded-2xl ${overallGrade.color} flex flex-col items-center justify-center text-white flex-shrink-0`}>
          <span className="text-3xl font-bold leading-none">{scores.overall}</span>
          <span className="text-xs opacity-80">/100</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-xl font-bold ${overallGrade.textColor}`}>
            {r(overallGrade.i18nKey)}
          </div>
          {scores.summary && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mt-1">
              {scores.summary}
            </p>
          )}
        </div>
      </div>

      {/* Dimension mini-badges */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {dimensions.map(({ key, score, grade }) => (
          <div
            key={key}
            className={`flex items-center justify-between px-3 py-2 rounded-lg ${grade.bgColor}`}
          >
            <span className="text-xs text-neutral-700 dark:text-neutral-300 truncate">
              {r(key)}
            </span>
            <span className={`text-sm font-bold ml-2 ${grade.textColor}`}>
              {score}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
